import NewProfileImage from '@/components/NewProfileImage';
import SvgIcon from '@/components/SvgIcon';
import Table from '@/components/Table/Table';
import { TooltipWrapper } from '@/components/TooltipWrapper';
import { SatisfactionCoachRanking } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { MediumText, RegularText } from '@/lib/Text';
import { Col, Row } from '@/lib/utils';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';

type Props = {
    satisfactionCoachRankings?: SatisfactionCoachRanking[];
    onClickAddTrainer?: () => void;
    onSelectCoach?: (coach: { code: string; value: string }) => void;
};

const columnHelper = createColumnHelper<SatisfactionCoachRanking>();

function FeedInstructorReviewsCoachRankings({
    satisfactionCoachRankings = [],
    onClickAddTrainer,
    onSelectCoach,
}: Props) {
    const formatRating = (value: number) => (value === 0 ? '-' : value.toFixed(1));
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'profile',

                header: () => (
                    <div className="flex flex-row w-full justify-start items-start h-[50px]">
                        <MediumText fontSize={18} lineHeight={25} color="#191F28" letterSpacing={-0.3}>
                            만족도 강사 순위
                        </MediumText>
                    </div>
                ),
                // 인덱스, 프로필 이미지, 이름
                cell: (info) => {
                    const rowIndex = info.table.getRowModel().rows.findIndex((r: any) => r.id === info.row.id) + 1;

                    return (
                        <Row className="flex flex-row w-full items-center justify-start pl-[10px]">
                            <MediumText
                                fontSize={20}
                                lineHeight={30}
                                color={colors.primary}
                                className="mr-[16px] min-w-[24px]"
                            >
                                {rowIndex}
                            </MediumText>
                            <NewProfileImage
                                size={38}
                                imageUrl={info.row.original.coachProfileImageUrl}
                                className="mr-[9px]"
                                circleColor={'#F2F4F6'}
                                imageFill="#D2D8DE"
                            />
                            <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} className="line-clamp-1">
                                {info.row.original.coachName}
                            </RegularText>
                        </Row>
                    );
                },
                size: 174,
                enableSorting: false,
                meta: {
                    align: 'start',
                    isSortIconVisible: false,
                },
            }),

            columnHelper.accessor('coachTotalAverageRating', {
                id: 'total',
                header: '전체',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'total';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <TooltipWrapper
                            tooltip={{
                                opacity: 1,
                                customContent: <Col>{/* TODO : 12명회원/100개의견  */}</Col>,
                            }}
                        >
                            <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                                {formatRating(info.getValue())}
                            </RegularText>
                        </TooltipWrapper>
                    );
                },
                size: 65,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachFirstClassAverageRating', {
                id: 'first',
                header: '첫',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'first';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                            {formatRating(info.getValue())}
                        </RegularText>
                    );
                },
                size: 65,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachMiddleClassAverageRating', {
                id: 'middle',
                header: '중간',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'middle';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                            {formatRating(info.getValue())}
                        </RegularText>
                    );
                },
                size: 65,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('coachLastClassAverageRating', {
                id: 'last',
                header: '마지막',
                cell: (info) => {
                    const table = info.table;
                    const sortedColumnId = table.getState().sorting?.[0]?.id;
                    const isSorting = table.getState().sorting.length > 0;
                    const isSortedColumn = sortedColumnId === 'last';
                    const color = isSorting ? (isSortedColumn ? colors.Text_1 : '#B9BBBD') : colors.Text_1;

                    return (
                        <RegularText fontSize={16} lineHeight={21} color={color} letterSpacing={-0.3}>
                            {formatRating(info.getValue())}
                        </RegularText>
                    );
                },
                size: 65,
                meta: {
                    align: 'center',
                },
            }),
        ],
        [],
    );

    return (
        <div className="flex w-[474px] h-[959px] flex-col bg-[#FFFFFF] border-[0.5px] border-[#E8E8E8] rounded-[25px] overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto px-[32px] pt-[21px] pb-[40px]">
                {/* 테이블 */}
                <Table
                    data={satisfactionCoachRankings}
                    columns={columns}
                    rowHeight={62}
                    headerColor="#FFFFFF"
                    onRowClick={(row: any) => {
                        if (onSelectCoach) {
                            console.log('wefwfe');

                            onSelectCoach({
                                code: row.coachId,
                                value: row.coachName,
                            });
                        }
                    }}
                    emptyComponent={
                        <Col className="w-full h-full justify-center items-center py-[60px]">
                            <RegularText
                                fontSize={18}
                                lineHeight={24}
                                color={colors.Text_1}
                                letterSpacing={-0.3}
                                className="mb-[23px]"
                            >
                                등록된 강사가 없어요
                            </RegularText>
                            <Row
                                className="bg-primary px-[20px] py-[14px] justify-center items-center rounded-[26px] cursor-pointer gap-[5.5px]"
                                onClick={onClickAddTrainer}
                            >
                                <SvgIcon name="SvgPlus" size={17} fill={colors.white} />
                                <MediumText fontSize={20} lineHeight={30} color={'white'}>
                                    강사 등록하기
                                </MediumText>
                            </Row>
                        </Col>
                    }
                    isUnballancedRowColor
                />
            </div>
        </div>
    );
}

export default FeedInstructorReviewsCoachRankings;
